import { CRYPTO_ASSETS, NETWORKS } from '../../../lib/constants'
import { formatMYR, formatCrypto, formatDate } from '../../../lib/utils'
import { layout, ui, cx, text, statusStyles, spinner } from '../../../lib/ui-primitives'
import { BackIcon, ExternalLinkIcon } from '../../icons'
import type { useKiosk } from '../../../hooks/useKiosk'

interface LookupScreenProps {
  kiosk: ReturnType<typeof useKiosk>
}

export function LookupScreen({ kiosk }: LookupScreenProps) {
  const {
    lookupOrderId,
    setLookupOrderId,
    lookupResult,
    lookupLoading,
    error,
    setError,
    lookupOrder,
    setScreen,
  } = kiosk

  const handleBack = () => {
    setScreen('welcome')
    setLookupOrderId('')
    setError('')
  }

  const getStatusStyle = (status: string) => {
    return statusStyles[status as keyof typeof statusStyles] || statusStyles.pending
  }

  return (
    <div className={layout.screenContent}>
      <button
        onClick={handleBack}
        className={ui.backBtn}
        aria-label="Go back to welcome screen"
      >
        <BackIcon aria-hidden="true" /> <span>Back</span>
      </button>

      <div className="text-center mb-6 sm:mb-8">
        <div className="text-4xl mb-3" aria-hidden="true">🔍</div>
        <h2 className={text.h2}>Track Order</h2>
        <p className={cx('text-sm mt-1', text.secondary)}>
          Enter your order ID to check status
        </p>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="w-full max-w-sm mx-auto">
          {/* Search Form */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={lookupOrderId}
              onChange={(e) => {
                setLookupOrderId(e.target.value.toUpperCase())
                setError('')
              }}
              placeholder="e.g., CKMKXXXX"
              className={cx(ui.input, 'flex-1 font-mono uppercase')}
              aria-label="Order ID"
            />
            <button
              onClick={lookupOrder}
              disabled={lookupLoading || !lookupOrderId.trim()}
              className={cx(
                ui.btnBase,
                'px-5 bg-indigo-600 hover:bg-indigo-500 text-white'
              )}
              aria-label="Search for order"
            >
              {lookupLoading ? (
                <div className={cx(spinner.small, spinner.base)} />
              ) : (
                'Search'
              )}
            </button>
          </div>

          {/* Error Message */}
          {error && !lookupResult && (
            <div className={cx(ui.error, 'mb-4')} role="alert">
              {error}
            </div>
          )}

          {/* Result Card */}
          {lookupResult && (
            <div className={cx(ui.card, 'p-4 sm:p-5')}>
              {/* Header with ID and Status */}
              <div className="flex items-center justify-between mb-4">
                <span className={cx('text-white font-bold', text.mono)}>
                  {lookupResult.id}
                </span>
                <span
                  className={cx(
                    'px-3 py-1 rounded-full text-xs font-medium',
                    getStatusStyle(lookupResult.status)
                  )}
                >
                  {lookupResult.status.toUpperCase()}
                </span>
              </div>

              {/* Order Details */}
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className={text.secondary}>Amount</span>
                  <span className={cx('text-white', text.numeric)}>
                    {formatMYR(lookupResult.amountMYR)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={text.secondary}>Crypto</span>
                  <span
                    className={cx('font-mono', text.numeric)}
                    style={{ color: CRYPTO_ASSETS[lookupResult.crypto]?.color }}
                  >
                    {formatCrypto(lookupResult.amountCrypto, 4)} {lookupResult.crypto}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={text.secondary}>Network</span>
                  <span className="text-white">{lookupResult.network}</span>
                </div>
                <div className="flex justify-between">
                  <span className={text.secondary}>Date</span>
                  <span className="text-white">{formatDate(lookupResult.createdAt)}</span>
                </div>

                {/* Transaction Hash */}
                {lookupResult.txHash && (
                  <div className={cx('pt-3', ui.divider)}>
                    <span className={cx('text-xs block mb-1', text.tertiary)}>
                      Transaction
                    </span>
                    <a
                      href={`${NETWORKS[lookupResult.network].explorer}${lookupResult.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cx(
                        'text-indigo-300 text-xs hover:underline inline-flex items-center gap-1',
                        text.mono
                      )}
                    >
                      View on explorer <ExternalLinkIcon aria-hidden="true" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
